/**
 * Inputs: trip GET/PUT JSON, optional HTTP validators.
 * Outputs: the blob concurrency token to send or compare.
 *
 * Netlify's CDN strips If-Match / If-None-Match before the function and may
 * overwrite ETag on the way back. Local Vite does neither. Tokens must travel
 * in JSON (and X-Trip-Etag) or the save uses document.revision.
 */

export const TRIP_ETAG_HEADER = "x-trip-etag";

export function normalizeEtag(value: string | null | undefined): string {
  if (!value) return "";
  return value.trim().replace(/^W\//i, "").replaceAll('"', "");
}

export function etagsMatch(left: string | null | undefined, right: string | null | undefined): boolean {
  const a = normalizeEtag(left);
  const b = normalizeEtag(right);
  return Boolean(a) && a === b;
}

export function readTripEtag(data: Record<string, unknown> | null | undefined, headerValue?: string | null): string | null {
  const fromBody = typeof data?.etag === "string" ? data.etag.trim() : "";
  const fromHeader = headerValue?.trim() ?? "";
  return fromBody || fromHeader || null;
}

export function isWrappedTripWrite(body: unknown): body is { trip: unknown; etag?: unknown } {
  if (!body || typeof body !== "object") return false;
  const row = body as Record<string, unknown>;
  if (!row.trip || typeof row.trip !== "object") return false;
  const inner = row.trip as Record<string, unknown>;
  return typeof inner.schemaVersion === "number" && (Array.isArray(inner.events) || Array.isArray(inner.bookings));
}

export function unwrapTripWrite(body: unknown): { doc: unknown; etag: string | null } {
  if (isWrappedTripWrite(body)) {
    return { doc: body.trip, etag: typeof body.etag === "string" ? body.etag : null };
  }
  return { doc: body, etag: null };
}

export function wrapTripWrite<T>(trip: T, etag: string): { trip: T; etag: string } {
  return { trip, etag };
}

export function clientTripToken(req: { headers: { get(name: string): string | null } }, bodyEtag?: string | null): string | null {
  const fromBody = bodyEtag?.trim() ?? "";
  const fromCustom = req.headers.get(TRIP_ETAG_HEADER)?.trim() ?? "";
  const fromIfMatch = req.headers.get("if-match")?.trim() ?? "";
  return fromBody || fromCustom || fromIfMatch || null;
}

export type CasDecision =
  | { kind: "create" }
  | { kind: "match"; etag: string }
  | { kind: "conflict"; etag: string };

export function decideTripCas(
  current: { etag: string; doc: { revision: number } } | null,
  clientToken: string | null,
  incomingRevision: number | null,
): CasDecision {
  if (!current) return { kind: "create" };
  if (clientToken && etagsMatch(clientToken, current.etag)) {
    return { kind: "match", etag: current.etag };
  }
  if (incomingRevision != null && incomingRevision === current.doc.revision) {
    return { kind: "match", etag: current.etag };
  }
  if (!clientToken && incomingRevision == null) {
    return { kind: "match", etag: current.etag };
  }
  return { kind: "conflict", etag: current.etag };
}

export function tripWriteHeaders(etag: string): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (etag) {
    headers["If-Match"] = etag;
    headers[TRIP_ETAG_HEADER] = etag;
  }
  return headers;
}
